// Description: Page to verify the email
// Updated: 12/09/2025
// Author: Simon Franklin

// React
import React, { useState, useEffect } from 'react';

// React Router
import { useNavigate } from 'react-router-dom';

// MUI - Componentes principais
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Grid,
    Button
} from '@mui/material';

/// Componentes
import EmailStep from './EmailStep';
import VerificationStep from './VerificationStep';
import FinalStep from './FinalStep';
import svg2 from "../../assets/ilustrations/Verify1.svg";

import { notify } from '../../utils/utils';
import { Refresh } from '../../api/aws/Refresh/RefreshToken';
import use123zapStore from '../../context/123zapStore';

const VerifyEmailPage = () => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [timeLeft, setTimeLeft] = useState(0);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [email, setEmail] = useState('');

    const userInfo = use123zapStore((state) => state.userInfo);

     useEffect(() => {
        if (code.length === 6) {
            handleSubmit({ preventDefault: () => { } });
        }
    }, [code, focusedIndex]);

    useEffect(() => {
        if (userInfo && userInfo['EMAIL'] !== null && activeStep === 0) {
            setEmail(userInfo['EMAIL']);
            if (emailInputValidation(userInfo['EMAIL'])) {
                setActiveStep(1);
            }
        }

    }, [userInfo]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        const finalCode = code.replace(/\s/g, '');
        setError('');

        if (finalCode.length === 6) {
            try {
                setLoading(true);

                const response = await fetch(`${process.env.REACT_APP_BACKEND_AWS_URL}/auth/confirm-email`, {
                    method: "PUT",
                    body: JSON.stringify({ email: email, code: finalCode })
                });

                const data = await response.json()

                if (data.status === "success") {
                    setCode('');

                    const rToken = localStorage.getItem("refreshToken");

                    if (!rToken) throw new Error("Token não encontrado!");

                    const refreshedData = await Refresh({ refresh_token: rToken });

                    if (refreshedData.id_token) {
                        const authTokens = {
                            accessToken: refreshedData.access_token,
                            refreshToken: rToken,
                            idToken: refreshedData.id_token ?? "",
                            expiresAt: Date.now() + refreshedData.expires_in * 1000,
                        };

                        localStorage.setItem("expiration_time", authTokens.expiresAt);
                        localStorage.setItem("jwt_token", authTokens.accessToken);
                        localStorage.setItem("idToken", authTokens.idToken);
                        localStorage.setItem("refreshToken", authTokens.refreshToken);

                        notify('Success', 'Seu e-mail foi verificado com sucesso!', true);

                        if (activeStep === 1) {
                            setActiveStep((prevActiveStep) => prevActiveStep + 1);
                        }
                    }

                } else if (data.message === 'Code not found') {
                    notify('Error', 'Código inválido', true);
                }

            } catch (error) {
                console.error('Erro ao verificar o código:', error);
                setError('Código inválido. Tente novamente.');
                setCode('');
                setFocusedIndex(0);
            } finally {
                setLoading(false);
            }

        } else {
            setError('Por favor, insira um código de 4 dígitos.');
        }
    };

    const handleResendCode = async () => {
        if (timeLeft === 0) {
            setCode('');
            setError('');

            try {
                setLoading(true);

                const res = await fetch(`${process.env.REACT_APP_BACKEND_AWS_URL}/auth/confirm-email`, {
                    method: "POST",
                    body: JSON.stringify({ email: email })
                });

                const response = await res.json();

                if (response.status === 'success') {
                    notify('Success', 'Código reenviado com sucesso!', true);
                }
            } catch (error) {
                console.error('Erro ao reenviar o código:', error);
                setError('Erro ao reenviar o código. Tente novamente mais tarde.');
            } finally {
                setLoading(false);
                setTimeLeft(30);
            }
        } else {
            setError('Aguarde até que o tempo acabe para reenviar o código.');
        }
    };

    useEffect(() => {
        if (timeLeft === 0) return;

        const interval = setInterval(() => {
            setTimeLeft((prevTime) => prevTime - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [timeLeft]);

    const emailInputValidation = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleNext = () => {
        if (activeStep === 0 && !emailInputValidation(email)) {
            notify('Alert', 'Por favor, preencha um e-mail válido.', true);
            return;
        } else {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep((prevActiveStep) => prevActiveStep - 1);
        }
    };

    const handleEmailChange = (value) => {
        setEmail(value);
    };

    const updateEmail = async () => {
        try {
            setLoading(true);
            if (!emailInputValidation(email !== '' ? email : userInfo['EMAIL'])) {
                notify('Error', 'Por favor, preencha um e-mail válido.', true);
                return;
            }

            const response = await fetch(`${process.env.REACT_APP_BACKEND_AWS_URL}/auth/confirm-email`, {
                method: "POST",
                body: JSON.stringify({ email: email !== '' ? email : userInfo['EMAIL'] })
            });

            const data = await response.json();

            if (data.status === 'success') {
                notify('Success', 'Código enviado com sucesso!', true);
                setTimeLeft(30);
                setActiveStep(1);
            }
        } catch (error) {
            console.error('Erro ao atualizar o e-mail:', error.message);
            notify('Error', error.message === 'O e-mail informado já está em uso.'
                ? 'O e-mail informado já está em uso.'
                : 'Erro ao atualizar o e-mail. Tente novamente mais tarde.', true);
        } finally {
            setLoading(false);
        }
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return <EmailStep TypeProvider={svg2} email={email} setEmail={handleEmailChange} />;
            case 1:
                return (
                    <VerificationStep
                        code={code}
                        setCode={setCode}
                        focusedIndex={focusedIndex}
                        setFocusedIndex={setFocusedIndex}
                        error={error}
                        timeLeft={timeLeft}
                        loading={loading}
                        handleSubmit={handleSubmit}
                        handleResendCode={handleResendCode}
                        email={email}
                    />
                );
            case 2:
                return (
                    <FinalStep />
                )
            default:
                return null;
        }
    };

    const steps = ['Seu e-mail', 'Código enviado', 'Código verificado'];

    return (
        <Grid
            container
            sx={{
                height: {
                    xs: '100%',
                    sm: 'calc(100dvh - var(--template-frame-height, 0px))',
                },
                mt: {
                    xs: 4,
                    sm: 0,
                },
                flexWrap: 'nowrap'
            }}
        >
            <Grid
                size={{ xs: 12, sm: 5, lg: 5 }}
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    backgroundColor: 'background.paper',
                    borderRight: { sm: 'none', md: '1px solid' },
                    borderColor: { sm: 'none', md: 'divider' },
                    alignItems: 'center',
                    pt: 16,
                    px: 10,
                    gap: 4,
                }}
            >
                <Box component='img' src={svg2} alt="code" className="responsive-svg" sx={{ width: { xs: '80%', sm: '400px' } }} />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        width: '100%',
                        maxWidth: 500,
                    }}
                >
                </Box>
            </Grid>
            <Grid
                size={{ sm: 12, md: 7, lg: 7 }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '100%',
                    width: '100%',
                    backgroundColor: { xs: 'transparent', sm: 'background.default' },
                    alignItems: 'start',
                    pt: { xs: 0, sm: 16 },
                    px: { xs: 2, sm: 10 },
                    gap: { xs: 4, md: 8 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: { sm: 'space-between', md: 'flex-end' },
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: { sm: '100%', md: 600 },
                    }}
                >
                    <Box
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            flexGrow: 1,
                        }}
                    >
                        <Stepper
                            id="desktop-stepper"
                            activeStep={activeStep}
                            sx={{ width: '100%', height: 40 }}
                        >
                            {steps.map((label) => (
                                <Step
                                    key={label}
                                >
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}

                        </Stepper>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        width: '100%',
                        maxWidth: { sm: '100%', md: 600 },
                        maxHeight: '720px',
                        gap: { xs: 5, md: 'none' },
                    }}
                >
                    <Stepper
                        id="mobile-stepper"
                        activeStep={activeStep}
                        alternativeLabel
                        sx={{ display: { sm: 'flex', md: 'none' } }}
                    >
                        {steps.map((label) => (
                            <Step
                                sx={{
                                    '& .MuiStepConnector-root': { top: { xs: 6, sm: 12 } },
                                }}
                                key={label}
                            >
                                <StepLabel
                                    sx={{ '.MuiStepLabel-labelContainer': { maxWidth: '70px' } }}
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    {activeStep === steps.length - 1 ? (
                        <FinalStep
                            handleNext={handleNext}
                            handleResendCode={handleResendCode}
                            activeStep={activeStep}
                        />
                    )
                        :
                        <React.Fragment>
                            {getStepContent(activeStep)}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                                    alignItems: 'end',
                                    flexGrow: 1,
                                    gap: 1,
                                    pb: { xs: 12, sm: 0 },
                                    mt: { xs: 2, sm: 0 },
                                    mb: '60px',
                                    justifyContent: 'space-between',
                                }}
                            >
                                {activeStep !== 0 ? (
                                    <>
                                        <Button
                                            onClick={handleBack}
                                            variant="outlined"
                                            sx={{ display: 'flex', width: { xs: '100%', sm: 'fit-content' }, padding: { xs: '6px 16px ', sm: '10px 20px' }, maxHeight: '44.5px' }}
                                        >
                                            Voltar
                                        </Button>
                                    </>
                                ) : (
                                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, width: '120px' }} />
                                )}

                                <Button
                                    variant="contained"
                                    onClick={activeStep === 1 ? handleResendCode : activeStep === 0 ? updateEmail : handleNext}
                                    sx={{ width: { xs: '100%', sm: 'fit-content' }, padding: { xs: '6px 16px ', sm: '10px 20px' } }}
                                    disabled={loading || timeLeft !== 0}
                                >
                                    {activeStep === steps.length - 1 ? 'Finalizar compra' : 'Próximo'}
                                </Button>
                            </Box>
                        </React.Fragment>


                    }
                </Box>
            </Grid>
        </Grid>
    );
};

export default VerifyEmailPage;
